import React, { useState, useEffect } from "react";
import AuthService from "../services/AuthService";
import useAuth from "../hooks/useAuth";
import { useNavigate, useLocation } from "react-router-dom";
import InputField from "../Login/InputField";
import ForgotPasswordLink from "./ForgotPasswordLink";
import ForgotPasswordModal from "./ForgotPasswordModal";
import VerifyOTP from "../Login/VerifyOTP";
import SignUpCard from "../Login/SignUpCard";
import { ToastContainer, toast } from "react-toastify";
import Header from "../common/Header";
import "react-toastify/dist/ReactToastify.css";
import "../styles/LoginForm.css";
import Swal from "sweetalert2";
import axios from "axios"; // Keep axios import if you plan to restore OAuth2 later
import envProperties from "../../config/envProperties";

const LoginForm = () => {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [usernameError, setUsernameError] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [showOtpCard, setShowOtpCard] = useState(false);
  const [activeTab, setActiveTab] = useState("login");
  const [isOtpSubmitting, setIsOtpSubmitting] = useState(false);
  const [isMsilSubmitting, setIsMsilSubmitting] = useState(false);

  const msilConfig = envProperties?.msil || {};

  // OAUTH2 specific states for MSIL Login
  // IMPORTANT: Replace with your actual values
  const [redirectUrl, setRedirectUrl] = useState(
    msilConfig.redirectUrl || "https://massbusinesszone.com/AdminHome/dashboard"
  );
  const [clientId, setClientId] = useState(
    msilConfig.clientId || "42gc3dtl45anvj24c9msbri04k"
  );
  const [msilLoginError, setMsilLoginError] = useState(null);

  const { login, role } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (location.pathname === "/signup") {
      setActiveTab("signup");
    } else {
      setActiveTab("login");
    }
    setShowModal(false);
    
    // CRITICAL FIX: Clear OTP verification status when visiting login page
    // This ensures MFA is required on every login attempt
    sessionStorage.removeItem('otpVerified');
  }, [location]);


  const handleLogin = async (event) => {
  event.preventDefault();
  if (isOtpSubmitting) return;
  setIsOtpSubmitting(true);

  setUsernameError("");
  setPasswordError("");

    if (!username.trim()) {
      setUsernameError("Please enter the MSPIN.");
      setIsOtpSubmitting(false);
      return;
    }

    if (!password.trim()) {
      setPasswordError("Please enter the password.");
      setIsOtpSubmitting(false);
      return;
    }
  
  // CRITICAL FIX: Clear MSIL data before MASS login to prevent role override
  console.log("[LoginForm] MASS login initiated - clearing MSIL data");
  localStorage.removeItem('msil_id_token');
  localStorage.removeItem('msil_access_token');
  localStorage.removeItem('msil_code_used');
  // Clear MSIL role from sessionStorage if present
  const existingRole = sessionStorage.getItem('role');
  if (existingRole && (existingRole === "MSIL" || existingRole === "SUPER_ADMIN" || existingRole === "ADMIN")) {
    sessionStorage.removeItem('role');
    console.log("[LoginForm] Cleared MSIL role:", existingRole);
  }
  // Clear MSIL email if present
  const existingEmail = sessionStorage.getItem('email');
  if (existingEmail && existingEmail.includes('@')) {
    sessionStorage.removeItem('email');
    console.log("[LoginForm] Cleared MSIL email:", existingEmail);
  }
  
  try {
    const otpData = await AuthService.login(username, password, "D");
    // Cache credentials needed only for OTP resend/verify fallback on this browser session.
    sessionStorage.setItem("otp_resend_username", username.trim());
    sessionStorage.setItem("otp_resend_password", password);
    // otpData should include: creationMessage, requestId, mobile, application, email, userName

     // Auto-close popup after 1 second (1000ms)
    Swal.fire({
      title: "Login Successful!",
      text: "Please verify OTP.",
      icon: "success",
      showConfirmButton: false, // Hide the OK button
      timer: 1000,               // Auto-close after 1 second
      timerProgressBar: true      // Optional: show progress bar
    });

    // Wait a bit to allow the alert to show before navigation
    setTimeout(() => {
      // Required for resend OTP: sendOtp needs credentials; otpData alone is not enough.
      navigate("/verifyOTP", {
        state: { otpData, username, password },
      });
    }, 1000);
  }
  catch (error) {
    // Get error message from API response
    let errorMessage = error.message || "Invalid login credentials. Please try again.";
    let showGenericError = false;
    
    // Check if it's an "already logged in" error - show generic popup
    if (errorMessage.includes("already Logged In") || 
        errorMessage.includes("already logged in") ||
        errorMessage.includes("Already Logged In")) {
      Swal.fire({
        title: "Session Active",
        text: "You are already logged in from another session. Please logout from the other session and try again.",
        icon: "warning",
        confirmButtonText: "OK",
      });
      setUsername("");
      setPassword("");
      setIsOtpSubmitting(false);
      return;
    }
    
    // Check if it's an onboarding-related error - show generic message (not "Invalid credentials")
    if (errorMessage.includes("onboarding") || 
        errorMessage.includes("ONBOARDING") ||
        errorMessage.includes("Dealer onboarding request not found") ||
        errorMessage.includes("Please complete onboarding first") ||
        errorMessage.includes("wait for approval")) {
      // Show generic error message for onboarding issues
      errorMessage = "Your account is not yet approved for login. Please contact support or wait for approval.";
      showGenericError = true;
    }
    // Check if it's a DMS API error - show generic message
    else if (errorMessage.includes("Error fetching user details from DMS API") || 
        errorMessage.includes("Error Fetching User Details from DMS API")) {
      errorMessage = "Invalid login credentials. Please try again.";
      showGenericError = false;
    }
    // Check if it's a "No User Details Found" error - show generic message
    else if (errorMessage.includes("No User Details Found") || 
             errorMessage.includes("No user details found")) {
      errorMessage = "Invalid login credentials. Please try again.";
      showGenericError = false;
    }
    // For any other error (bad credentials, etc.) - show generic message
    else {
      errorMessage = "Invalid login credentials. Please try again.";
      showGenericError = false;
    }
    
    Swal.fire({
      title: showGenericError ? "Access Denied" : "Error",
      text: errorMessage,
      icon: showGenericError ? "info" : "error",
      confirmButtonText: "OK",
    });
    setUsername("");
    setPassword("");
  } finally {
  setIsOtpSubmitting(false);
  }
};


  // Updated MSIL login to direct login without OAuth2 flow
  const handleMsilLogin = async () => {
    setMsilLoginError(null);
    setIsMsilSubmitting(true);
    
    // CRITICAL FIX: Clear ALL MASS data before initiating MSIL login
    // This prevents MASS user data from persisting when switching to MSIL Admin
    console.log("[LoginForm] MSIL Admin login button clicked - clearing ALL MASS data");
    
    // Clear MASS sessionStorage data
    const existingRole = sessionStorage.getItem('role');
    if (existingRole && (existingRole === "MASS_ADMIN" || existingRole === "MASS_EXECUTIVE")) {
      sessionStorage.removeItem('role');
      sessionStorage.removeItem('token');
      sessionStorage.removeItem('uid');
      sessionStorage.removeItem('otpVerified');
      console.log("[LoginForm] Cleared MASS sessionStorage role:", existingRole);
    }
    
    // Clear ALL MASS-related localStorage items
    const massLocalStorageKeys = [
      'dealerCode',
      'dealerInfo',
      'empName',
      'forCode',
      'locationCode',
      'multiServiceDealerCode',
      'outletCode'
    ];
    
    massLocalStorageKeys.forEach(key => {
      if (localStorage.getItem(key)) {
        localStorage.removeItem(key);
        console.log(`[LoginForm] Cleared MASS localStorage key: ${key}`);
      }
    });
    
    // Clear any other MASS-related items
    Object.keys(localStorage).forEach(key => {
      if (!key.startsWith('msil_') && 
          key !== 'fundcheck_rate_limit' && 
          (massLocalStorageKeys.includes(key) || 
           key.toLowerCase().includes('dealer') || 
           key.toLowerCase().includes('mass') ||
           key.toLowerCase().includes('emp'))) {
        localStorage.removeItem(key);
        console.log(`[LoginForm] Cleared additional MASS localStorage key: ${key}`);
      }
    });
    
    try {
      // In handleMsilLogin - updated redirect URL
      const response = await axios.post(
        msilConfig.federatedLoginUrl ||
          "https://massbusinesszone.com/api/auth/federated-login",
        {
          redirectUrl:
            msilConfig.redirectUrl ||
            "https://massbusinesszone.com/AdminHome/dashboard", // ✅Updated
          clientId: msilConfig.clientId || "42gc3dtl45anvj24c9msbri04k",
        },
        {
          headers: { "Content-Type": "application/json" },
        }
      );

      const loginUrl = response.data?.data;
      if (loginUrl) {
        window.location.href = loginUrl;
      } else {
        throw new Error("No login URL returned from backend.");
      }
    } catch (err) {
    setMsilLoginError("MSIL login failed. Please try again.");
  Swal.fire({
    title: "MSIL Login Failed",
    text: "Please try again.",
    icon: "error",
    showConfirmButton: false,
    timer: 1000,
    timerProgressBar: true,
  });    } finally {
    setIsMsilSubmitting(false);
  }
};

  const handleTabSwitch = (tab) => {
    setActiveTab(tab);
    navigate(tab === "signup" ? "/signup" : "/login");
  };

  const handleShowModal = (e) => {
    e.preventDefault();
    setShowModal(true);
  };

  const handleCloseModal = () => setShowModal(false);

  return (
    <div className="login-page">
      <div className="logo-container">
        <Header />
      </div>

      <div className="login-container">
        <div className="login-card">
          <h1 className="mass-business-zone-heading">MARUTI SUZUKI DIGITAL BUSINESS HUB</h1>
          {showOtpCard && activeTab === "login" && role === "MASS" ? (
            <VerifyOTP />
          ) : (
            <>
              <div className="text-center mb-4 toggle-tabs">
                <span
                  className={
                    activeTab === "login" ? "active-tab" : "inactive-tab"
                  }
                  onClick={() => handleTabSwitch("login")}
                  style={{ marginRight: "15px", cursor: "pointer" }}
                >
                  Log In
                </span>
                <span
                  className={
                    activeTab === "signup" ? "active-tab" : "inactive-tab"
                  }
                  onClick={() => handleTabSwitch("signup")}
                  style={{ cursor: "pointer" }}
                >
                  Sign Up
                </span>
              </div>

              {activeTab === "login" && (
  <>
    {/* LOGIN FORM — ONLY Send OTP */}
    <form onSubmit={handleLogin} className="w-100">
      <InputField
        label="MSPIN"
        type="text"
        value={username}
        onChange={(e) =>
          e.target.value.length <= 50 && setUsername(e.target.value)
        }
        placeholder="Enter MSPIN"
        errorMessage={usernameError}
        maxLength={50}
      />

      <InputField
        label="Password"
        type="password"
        value={password}
        onChange={(e) =>
          e.target.value.length <= 50 && setPassword(e.target.value)
        }
        placeholder="Enter password"
        errorMessage={passwordError}
        maxLength={50}
      />

      <ForgotPasswordLink onClick={handleShowModal} />

      <button
        type="submit"
        className="btn btn-primary login-button"
        disabled={isOtpSubmitting}
      >
        {isOtpSubmitting ? "Processing..." : "Send OTP"}
      </button>
    </form>

    {/* MSIL LOGIN BUTTON — COMPLETELY OUTSIDE THE FORM */}
    <div className="text-center mt-3 msil-btn-wrapper">
      {msilLoginError && (
        <p style={{ color: "red" }}>{msilLoginError}</p>
      )}

      <button
        type="button"
        className="btn btn-primary w-100"
        onClick={handleMsilLogin}
        disabled={isMsilSubmitting}
      >
         {isMsilSubmitting ? "Please wait..." : "Login via MSIL Admin"}
      </button>
    </div>
  </>
)}

              {activeTab === "signup" && <SignUpCard />}
            </>
          )}
        </div>
      </div>

      <ForgotPasswordModal show={showModal} handleClose={handleCloseModal} />
      <ToastContainer />
    </div>
  );
};

export default LoginForm;
